import { useState } from 'react';
import { useLanguage } from '@/lib/i18n';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { ClipboardList, Search, Mail, Check, Loader2, Send } from 'lucide-react';
import ScrollReveal from '@/components/ScrollReveal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FormInput({ label, id, type = "text", required, color, ...props }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-white/50 mb-2">
        {label}{required && <span className={`text-[${color || '#7F00FF'}] ml-1`}>*</span>}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        className={`w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white text-sm outline-none placeholder:text-white/20 focus:border-[${color || '#7F00FF'}]/50 focus:ring-1 focus:ring-[${color || '#7F00FF'}]/30 transition-colors duration-200`}
        data-testid={`form-input-${id}`}
        {...props}
      />
    </div>
  );
}

function FormTextarea({ label, id, required, color, ...props }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-white/50 mb-2">
        {label}{required && <span className={`text-[${color || '#7F00FF'}] ml-1`}>*</span>}
      </label>
      <textarea
        id={id}
        required={required}
        rows={4}
        className={`w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white text-sm outline-none placeholder:text-white/20 focus:border-[${color || '#7F00FF'}]/50 focus:ring-1 focus:ring-[${color || '#7F00FF'}]/30 transition-colors duration-200 resize-none`}
        data-testid={`form-textarea-${id}`}
        {...props}
      />
    </div>
  );
}

function FormSelect({ label, id, required, options, color, ...props }) {
  const { t } = useLanguage();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-white/50 mb-2">
        {label}{required && <span className={`text-[${color || '#7F00FF'}] ml-1`}>*</span>}
      </label>
      <select
        id={id}
        required={required}
        className={`w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[${color || '#7F00FF'}]/50 focus:ring-1 focus:ring-[${color || '#7F00FF'}]/30 transition-colors duration-200 appearance-none cursor-pointer`}
        data-testid={`form-select-${id}`}
        {...props}
      >
        <option value="" className="bg-[#0A0A0A]">{t('form_select_placeholder')}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value} className="bg-[#0A0A0A]">{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function SuccessState({ t, onReset }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="form-success">
      <div className="w-16 h-16 rounded-full bg-[#7F00FF]/10 border border-[#7F00FF]/20 flex items-center justify-center mb-6">
        <Check className="w-7 h-7 text-[#7F00FF]" />
      </div>
      <h3 className="font-syne text-2xl font-bold mb-3">{t('form_success_title')}</h3>
      <p className="text-white/35 max-w-sm mb-8">{t('form_success_desc')}</p>
      <button
        onClick={onReset}
        data-testid="form-send-another-btn"
        className="text-sm text-[#00E5FF]/60 hover:text-[#00E5FF] transition-colors duration-200"
      >
        {t('form_send_another')}
      </button>
    </div>
  );
}

function ProjectBriefForm() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    try {
      await axios.post(`${API}/briefs`, data);
      setSuccess(true);
    } catch {
      setError(t('form_error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) return <SuccessState t={t} onReset={() => setSuccess(false)} />;

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="project-brief-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormInput label={t('form_name')} id="brief-name" name="name" required placeholder={t('form_name_placeholder')} color="#7F00FF" />
        <FormInput label={t('form_email')} id="brief-email" name="email" type="email" required placeholder={t('form_email_placeholder')} color="#7F00FF" />
        <FormInput label={t('form_insta')} id="brief-instagram" name="instagram" type="text" required placeholder={t('form_insta_placeholder')} color="#7F00FF" />
        <FormInput label={t('form_tel')} id="brief-tel" name="tel" type="tel" placeholder={t('form_tel_placeholder')} color="#7F00FF" />
      </div>
      <div className=" gap-5">
        <FormSelect label={t('form_project_type')} id="brief-type" name="project_type" required options={[
          { value: 'beat', label: t('form_type_beat') },
          { value: 'mixmastering', label: t('form_type_mix_mastering') },
          { value: 'full', label: t('form_type_full') },
          { value: 'mix', label: t('form_type_mix') },
          { value: 'mastering', label: t('form_type_mastering') },
        ]} />
      </div>
      <FormInput label={t('form_project_name')} id="project-name" name="project_name" placeholder={t('form_project_name_placeholder')} />
      <FormTextarea label={t('form_description')} id="brief-desc" name="description" placeholder={t('form_description_placeholder')} />
      <FormInput label={t('form_deadline')} id="brief-deadline" name="deadline" placeholder={t('form_deadline_placeholder')} color="#7F00FF" />
      {error && <p className="text-red-400 text-sm" data-testid="brief-form-error">{error}</p>}
      <button
        type="submit"
        disabled={loading}
        data-testid="submit-brief-btn"
        className="w-full flex items-center justify-center gap-2 rounded-full bg-white text-black font-bold px-8 py-3.5 hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 shadow-[0_0_25px_rgba(255,255,255,0.08)] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {loading ? t('form_submitting') : t('form_submit')}
      </button>
    </form>
  );
}

const steps = [
  { num: 1, key: 'step1', icon: ClipboardList },
  { num: 2, key: 'step2', icon: Search },
  { num: 3, key: 'step3', icon: Mail },
];

export default function GoogleFormTabs() {
  const { t } = useLanguage();

  return (
    <div data-testid="google-form-tabs">
      <Tabs defaultValue="project" className="w-full">
        <TabsContent value="project">
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
            <div className="space-y-6">
              {steps.map((step, i) => (
                <ScrollReveal key={step.num} delay={i * 0.1}>
                  <div className="flex gap-4 items-start mb-2" data-testid={`form-step-${step.num}`}>
                    <div className="w-10 h-10 rounded-full bg-[#7F00FF]/[0.08] border border-[#7F00FF]/20 flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-4 h-4 text-[#7F00FF]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{t(`requests_step${step.num}_title`)}</p>
                      <p className="text-xs text-white/30 mt-1 leading-relaxed">{t(`requests_step${step.num}_desc`)}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
            <div className="bg-white/[0.015] border border-white/[0.06] rounded-2xl p-6 md:p-8">
              <ProjectBriefForm />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
